import { DownloadOutlined, ExportOutlined, FilePdfOutlined, HistoryOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Rendition, RenditionMissing, RenditionTemplateOption, RenditionsAvailable } from "@wms/domain";
import { Alert, App, Button, Grid, Modal, Radio, Result, Space, Spin, Table, Tabs, Tag, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../api/format";
import { openPortalPrint } from "../api/portal";
import { openPrintForm } from "../api/print";
import {
  ACCOUNTING_REVIEW_LABELS,
  PURPOSE_LABELS,
  type Channel,
  type ViewerProblem,
  matchesLegacy,
  newRequestKey,
  pagesWord,
  pickTemplate,
  renditionsApi,
  saveBlob,
  sha256Hex,
  sizeWord,
  viewerProblem,
} from "../api/renditions";
import { errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";

export interface DocumentViewerProps {
  open: boolean;
  onClose: () => void;
  /** The kind of document on the server (`expected_receipt`, `invoice`, `report.stock` …). */
  kind: string;
  documentId: string;
  /** Report parameters (the filters of the report screen); the documents have none. */
  params?: Record<string, unknown> | null;
  /** The code of the old print button (`torg12`): the layout that replaced it is chosen first. */
  legacyForm?: string | null;
  /** The old HTML form (`/print-forms/torg12/{id}`): reachable while a kind or a layout is not connected (TMP24). */
  legacyPath?: string | null;
  channel?: Channel;
  /** Said in the title until the server names the document: «Поступление A000123». */
  title?: string;
}

/** Phones and tablets show no PDF inside a page and print nothing from a hidden frame (TMP22, AT27). */
function isMobileDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

/**
 * «Печать» of the stored file: the same bytes as the preview go into a hidden frame and the browser's print dialog is
 * called on it. Resolves when the dialog was asked for; rejects when the browser refuses (the caller offers download).
 */
function printInHiddenFrame(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("data-testid", "docviewer-print-frame");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      frame.remove();
      reject(new Error("Браузер не загрузил файл для печати"));
    }, 20_000);
    frame.onload = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try {
        const win = frame.contentWindow;
        if (!win) throw new Error("Браузер не дал доступа к файлу для печати");
        win.focus();
        win.print();
        resolve();
      } catch (e) {
        reject(e instanceof Error ? e : new Error("Браузер не открыл диалог печати"));
      } finally {
        // the dialog may still be open: the frame lives a minute, then goes
        window.setTimeout(() => frame.remove(), 60_000);
      }
    };
    frame.src = url;
    document.body.appendChild(frame);
  });
}

/** UI04: what is not filled, where it is corrected and whether this person may do it. */
function MissingTable({ missing, channel, onNavigate }: { missing: RenditionMissing[]; channel: Channel; onNavigate: () => void }) {
  return (
    <Table<RenditionMissing>
      size="small"
      rowKey="field"
      pagination={false}
      dataSource={missing}
      scroll={{ x: true }}
      data-testid="docviewer-missing"
      columns={[
        { title: "Что не заполнено", dataIndex: "label" },
        { title: "Где исправить", dataIndex: "fix", render: (v: string | null) => v ?? "—" },
        {
          title: "Доступ",
          key: "access",
          render: (_: unknown, m: RenditionMissing) => {
            if (channel === "portal") return "Обратитесь к складу, который ведёт ваши документы";
            if (m.can_fix && m.route)
              return (
                <Link to={m.route} onClick={onNavigate} data-testid={`docviewer-fix-${m.field}`}>
                  Открыть и заполнить
                </Link>
              );
            if (m.can_fix) return "Можно исправить в указанном месте";
            return "Нужно право изменения — обратитесь к ответственному сотруднику";
          },
        },
      ]}
    />
  );
}

/** A refusal or a failure in words, with the request id of a failure and the old form when it can help. */
function ProblemView({
  problem,
  onRetry,
  legacy,
  canSeeTemplates,
  onNavigate,
}: {
  problem: ViewerProblem;
  onRetry?: () => void;
  legacy?: (() => void) | null;
  canSeeTemplates: boolean;
  onNavigate: () => void;
}) {
  const status = problem.kind === "forbidden" && problem.title !== "Выберите склад" ? "403" : problem.kind === "not_found" ? "404" : problem.kind === "error" ? "error" : "warning";
  const retry = onRetry && (problem.kind === "offline" || problem.kind === "error");
  return (
    <div data-testid={`docviewer-state-${problem.kind}`} style={{ width: "100%" }}>
    <Result
      status={status}
      title={problem.title}
      subTitle={
        <>
          <span data-testid="docviewer-problem-detail">{problem.detail}</span>
          {problem.reference && (
            <span style={{ display: "block", marginTop: 6 }} data-testid="docviewer-reference">
              Код обращения: <Typography.Text copyable>{problem.reference}</Typography.Text>
            </span>
          )}
        </>
      }
      extra={
        <Space wrap style={{ justifyContent: "center" }}>
          {retry && (
            <Button type="primary" icon={<ReloadOutlined />} onClick={onRetry} data-testid="docviewer-retry">
              Повторить
            </Button>
          )}
          {legacy && (problem.kind === "not_connected" || problem.kind === "no_template" || problem.kind === "error") && (
            <Button onClick={legacy} data-testid="docviewer-legacy-open">
              Открыть прежнюю печатную форму
            </Button>
          )}
          {problem.kind === "no_template" && canSeeTemplates && (
            <Link to="/document-templates" onClick={onNavigate}>
              <Button>Шаблоны документов</Button>
            </Link>
          )}
        </Space>
      }
    />
    </div>
  );
}

function TemplateTags({ option }: { option: RenditionTemplateOption }) {
  return (
    <>
      {/* the cabinet gets no administrative fields of a layout (TMP21): an absent purpose draws nothing */}
      {option.purpose && option.purpose !== "operational" && <Tag bordered={false}>{PURPOSE_LABELS[option.purpose] ?? option.purpose}</Tag>}
      {/* D1 (30.10.2026): the catalogue mark of an accounting layout in work before the accountant's check */}
      {option.accounting_review === "pending" && (
        <Tag color="processing" bordered={false} data-testid="docviewer-accounting-pending">
          {ACCOUNTING_REVIEW_LABELS.pending}
        </Tag>
      )}
      {option.version === null && <Tag color="warning" bordered={false}>нет версии на дату документа</Tag>}
      {option.version !== null && option.missing.length > 0 && (
        <Tag color="warning" bordered={false}>
          не хватает данных
        </Tag>
      )}
    </>
  );
}

/**
 * UI03 «Документы и печать» (KZ-DOC TMP17–TMP19): the document opens in a preview of its stored PDF; «Скачать PDF» and
 * «Печать» take that same file — one rendition id, one SHA-256 (AT06). Forming a file never changes the document
 * (AT08); a repeat sends the same request key (AT07). The states are told apart: loading, forming, ready, missing
 * requisites, no access, no layout, failure with its request id, outdated data, cancelled document, no network.
 */
export function DocumentViewer(props: DocumentViewerProps) {
  const { open, onClose } = props;
  const screens = Grid.useBreakpoint();
  const narrow = screens.md === false;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={narrow ? "100%" : "min(1320px, 96vw)"}
      style={narrow ? { top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 } : { top: 20 }}
      styles={{ body: { paddingTop: 4 } }}
      title={null}
      className="wms-docviewer-modal"
    >
      {open && <ViewerBody {...props} narrow={narrow} />}
    </Modal>
  );
}

function ViewerBody({ kind, documentId, params, legacyForm, legacyPath, channel = "staff", title, onClose, narrow }: DocumentViewerProps & { narrow: boolean }) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);
  const [tab, setTab] = useState("view");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** A file chosen in «История» (an earlier version) instead of the latest of the layout. */
  const [override, setOverride] = useState<Rendition | null>(null);
  const [created, setCreated] = useState<Record<string, Rendition>>({});
  const [printNote, setPrintNote] = useState<{ type: "info" | "warning"; text: string } | null>(null);
  const autoFormed = useRef(new Set<string>());
  /** TMP12: the key of the current intent to form; kept after an unknown result so the repeat finds the same file. */
  const pendingKey = useRef<string | null>(null);
  const mobile = isMobileDevice();

  const availableKey = ["renditions", channel, kind, documentId, paramsKey];
  const available = useQuery({
    queryKey: availableKey,
    queryFn: () => renditionsApi.available(channel, kind, documentId, params),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const options = available.data?.templates ?? [];
  const selected = options.find((o) => o.template_id === selectedId) ?? pickTemplate(options, legacyForm);
  const legacyMissing = !!legacyForm && options.length > 0 && !options.some((o) => matchesLegacy(o, legacyForm));
  // D3: a report is opened right after «Сформировать» — an outdated stored report gives way to a fresh file; a
  // document keeps showing its stored file until «Сформировать заново» (TMP05)
  const isReport = kind.startsWith("report.");
  const latest = selected?.latest && !(isReport && selected.latest.current === false) ? selected.latest : null;
  const shown: Rendition | null = override ?? (selected ? created[selected.template_id] ?? latest : null);

  // TMP24: the old HTML form stays one click away (the cabinet opens it with its own token)
  const openLegacy = legacyPath
    ? () => (channel === "portal" ? openPortalPrint(legacyPath) : openPrintForm(legacyPath)).catch((e) => message.error(errorMessage(e)))
    : null;

  const form = useMutation({
    mutationFn: (option: RenditionTemplateOption) => {
      if (!pendingKey.current) pendingKey.current = newRequestKey();
      return renditionsApi.create(channel, kind, documentId, { template_id: option.template_id, params: params ?? {}, request_key: pendingKey.current });
    },
    onSuccess: (row, option) => {
      const previous = created[option.template_id] ?? option.latest;
      pendingKey.current = null;
      setOverride(null);
      setCreated((c) => ({ ...c, [option.template_id]: row }));
      if (previous && previous.id === row.id) message.info("Данные документа не менялись — это тот же сохранённый файл");
      queryClient.invalidateQueries({ queryKey: ["renditions", channel, kind, documentId] });
      queryClient.invalidateQueries({ queryKey: ["renditions-history", kind, documentId] });
    },
    onError: (error) => {
      const problem = viewerProblem(error, navigator.onLine);
      // the result of a request that never came back is unknown: the repeat goes with the same key (TMP12)
      if (problem.kind !== "offline") pendingKey.current = null;
    },
  });

  // TMP17: the file is formed when the document is opened and nothing is stored yet; the server checks rights,
  // scope and requisites first; the same data and layout give back the stored file
  useEffect(() => {
    if (!selected || shown || selected.version === null || selected.missing.length || form.isPending) return;
    if (autoFormed.current.has(selected.template_id)) return;
    autoFormed.current.add(selected.template_id);
    form.mutate(selected);
  }, [selected, shown, form]);

  const pdf = useQuery({
    queryKey: ["rendition-pdf", channel, shown?.id],
    queryFn: async () => {
      const file = await renditionsApi.pdf(channel, shown!.id, false);
      const actual = await sha256Hex(file.blob);
      return { ...file, actual };
    },
    enabled: !!shown,
    retry: false,
    staleTime: Infinity,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
  });
  // one object URL per loaded file, revoked with it (an effect, so React's double mount in development keeps a live URL)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pdf.data) {
      setPdfUrl(null);
      return;
    }
    const url = URL.createObjectURL(pdf.data.blob);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdf.data]);
  const fileSha = pdf.data?.sha256 ?? shown?.pdf_sha256 ?? null;
  const shaOk = pdf.data?.actual ? pdf.data.actual === fileSha : null;

  const history = useQuery({
    queryKey: ["renditions-history", kind, documentId, paramsKey],
    queryFn: () => renditionsApi.history(kind, documentId, params),
    enabled: channel === "staff" && tab === "history",
    retry: false,
  });

  const download = useMutation({
    mutationFn: async () => {
      const file = await renditionsApi.pdf(channel, shown!.id, true);
      const actual = await sha256Hex(file.blob);
      // AT06: the download is the file of the preview; a different hash is a transfer fault, never a silent swap
      if (actual && file.sha256 && actual !== file.sha256) throw new Error("Файл повреждён при передаче: контрольная сумма не совпала. Повторите скачивание.");
      if (actual && fileSha && actual !== fileSha) throw new Error("Сервер отдал не тот файл, что показан в просмотре. Сформируйте документ заново.");
      saveBlob(file.blob, file.filename ?? shown!.filename);
      return file;
    },
    onSuccess: () => {
      message.success("PDF скачан — тот же файл, что в просмотре");
      queryClient.invalidateQueries({ queryKey: ["renditions-history", kind, documentId] });
    },
    onError: (e) => message.error(viewerProblem(e, navigator.onLine).detail),
  });

  const print = useMutation({
    mutationFn: async () => {
      if (!pdfUrl || !shown) throw new Error("Файл ещё не загружен");
      if (mobile) {
        // AT27: a phone opens the stored file in its own viewer, printing is done from there
        window.open(pdfUrl, "_blank", "noopener");
        return "mobile" as const;
      }
      await printInHiddenFrame(pdfUrl);
      await renditionsApi.printDialog(channel, shown.id);
      return "dialog" as const;
    },
    onSuccess: (how) => {
      if (how === "mobile") setPrintNote({ type: "info", text: "PDF открыт отдельно: печать — из меню просмотрщика («Поделиться» → «Печать»). Если печать недоступна, скачайте файл." });
      else {
        setPrintNote({ type: "info", text: "Открыт диалог печати для этого же файла. Факт печати система не подтверждает — в истории это «Открыт диалог печати»." });
        queryClient.invalidateQueries({ queryKey: ["renditions-history", kind, documentId] });
      }
    },
    onError: (e) =>
      setPrintNote({
        type: "warning",
        text: `Браузер не открыл печать (${errorMessage(e)}). Скачайте PDF и распечатайте его из программы просмотра.`,
      }),
  });

  if (available.isLoading) {
    return (
      <div className="wms-docviewer-state" data-testid="docviewer-state-loading">
        <Spin size="large" />
        <Typography.Text type="secondary">Открываем {title ? `«${title}»` : "документ"}…</Typography.Text>
      </div>
    );
  }
  if (available.isError) {
    const problem = viewerProblem(available.error, navigator.onLine);
    return (
      <div data-testid="docviewer">
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          Документы и печать{title ? ` · ${title}` : ""}
        </Typography.Title>
        <ProblemView problem={problem} onRetry={() => available.refetch()} legacy={openLegacy} canSeeTemplates={can("document_templates.read")} onNavigate={onClose} />
      </div>
    );
  }
  const data = available.data as RenditionsAvailable;
  // the code of a report is technical (TMP02): the report is named by its title only
  const heading = `${data.kind_label}${data.number && !(isReport && data.number === documentId) ? ` ${data.number}` : ""}`;
  const formProblem = form.isError ? viewerProblem(form.error, navigator.onLine) : null;
  const missing = selected?.missing.length ? selected.missing : formProblem?.kind === "missing" ? formProblem.missing ?? [] : [];
  // a stored file on screen may always be downloaded and printed — it is exactly what the preview shows (TMP18)
  const blockedReason =
    shown && pdfUrl
      ? null
      : !selected
        ? "Для этого документа нет макетов"
        : selected.version === null && !shown
          ? "Нет опубликованной версии макета на дату документа"
          : missing.length && !shown
            ? "Сначала заполните недостающие данные выбранного макета"
            : !shown
              ? form.isPending
                ? "PDF формируется"
                : "PDF ещё не сформирован"
              : pdf.isError
                ? "Файл не загрузился"
                : "Файл загружается";

  const side = (
    <div className="wms-docviewer-side">
      <div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Вид документа
        </Typography.Text>
        <Typography.Title level={5} style={{ margin: 0 }} data-testid="docviewer-heading">
          {heading}
        </Typography.Title>
        <Space size={[4, 4]} wrap style={{ marginTop: 6 }}>
          {data.status && <Tag>{data.status}</Tag>}
          {data.cancelled && (
            <Tag color="error" data-testid="docviewer-cancelled">
              Документ отменён
            </Tag>
          )}
        </Space>
        {data.organization && (
          <div style={{ marginTop: 6 }}>
            <Typography.Text type="secondary">Наша организация: </Typography.Text>
            <Typography.Text data-testid="docviewer-organization">{data.organization}</Typography.Text>
          </div>
        )}
      </div>

      {options.length > 1 ? (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
            Макет
          </Typography.Text>
          <Radio.Group
            value={selected?.template_id}
            onChange={(e) => {
              setSelectedId(e.target.value);
              setOverride(null);
              setPrintNote(null);
              form.reset();
            }}
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
            data-testid="docviewer-templates"
          >
            {options.map((o) => (
              <Radio key={o.template_id} value={o.template_id} data-testid={`docviewer-template-${o.code ?? o.template_id}`}>
                <span>{o.name}</span> <TemplateTags option={o} />
              </Radio>
            ))}
          </Radio.Group>
        </div>
      ) : selected ? (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            Макет
          </Typography.Text>
          <span>{selected.name}</span> <TemplateTags option={selected} />
        </div>
      ) : null}

      {legacyMissing && legacyForm && (
        <Alert
          type="info"
          showIcon
          message="Для этой кнопки нового макета пока нет"
          description={selected ? `Открыт макет «${selected.name}». Прежняя печатная форма доступна ниже.` : undefined}
          data-testid="docviewer-legacy-missing"
        />
      )}

      {selected && (
        <div className="wms-docviewer-facts">
          {(override?.template_version ?? selected.version) !== null && (
            <div data-testid="docviewer-template-version">Макет: версия {override?.template_version ?? shown?.template_version ?? selected.version}</div>
          )}
          {shown && (
            <>
              <Tag color="success" icon={<FilePdfOutlined />} style={{ marginTop: 6 }} data-testid="docviewer-ready">
                PDF готов · {pagesWord(shown.pages)}
                {shown.size ? ` · ${sizeWord(shown.size)}` : ""}
              </Tag>
              <div style={{ color: "var(--wms-muted)", fontSize: 12, marginTop: 4 }}>
                Сформирован {formatDateTime(shown.created_at)}
                {shown.created_by ? ` · ${shown.created_by}` : ""}
              </div>
              {shaOk === true && (
                <div style={{ color: "var(--wms-muted)", fontSize: 12 }} title={`SHA-256 ${fileSha}`} data-testid="docviewer-sha-ok">
                  Контрольная сумма файла проверена
                </div>
              )}
              {shaOk === false && <Alert type="error" showIcon style={{ marginTop: 6 }} message="Контрольная сумма показанного файла не совпала с сохранённой — сформируйте заново" />}
            </>
          )}
        </div>
      )}

      {override && (
        <Alert
          type="info"
          showIcon
          message={`Показан файл из истории: версия макета ${override.template_version}, ${formatDateTime(override.created_at)}`}
          action={
            <Button size="small" onClick={() => setOverride(null)} data-testid="docviewer-back-latest">
              К последнему
            </Button>
          }
        />
      )}
      {!override && shown?.current === false && (
        <Alert
          type="warning"
          showIcon
          message="Данные документа изменились — сформируйте заново"
          description="Показан ранее сохранённый файл: он не меняется. Новый PDF возьмёт текущие данные и версию макета."
          data-testid="docviewer-outdated"
        />
      )}
      {shown && missing.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Для нового файла не хватает данных"
          description={`${missing.map((m) => m.label).join(", ")}. Показан ранее сохранённый файл.`}
          data-testid="docviewer-missing-short"
        />
      )}
      {data.cancelled && <Alert type="warning" showIcon message="Документ отменён" description="Файл показывает документ как есть; для складской работы отменённый документ не используется." />}

      <div className="wms-docviewer-actions">
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          disabled={!!blockedReason}
          loading={download.isPending}
          onClick={() => download.mutate()}
          data-testid="docviewer-download"
          block
        >
          Скачать PDF
        </Button>
        <Button icon={<PrinterOutlined />} disabled={!!blockedReason} loading={print.isPending} onClick={() => print.mutate()} data-testid="docviewer-print" block>
          Печать
        </Button>
        {selected && selected.version !== null && (
          <Button
            icon={<ReloadOutlined />}
            disabled={!!selected.missing.length || form.isPending}
            loading={form.isPending}
            onClick={() => {
              setPrintNote(null);
              form.mutate(selected);
            }}
            data-testid="docviewer-reform"
            block
          >
            Сформировать заново
          </Button>
        )}
        {blockedReason ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }} data-testid="docviewer-blocked-reason">
            {blockedReason}
          </Typography.Text>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {mobile ? "На телефоне печать — из открытого PDF." : "Печать откроет диалог браузера для этого же файла."} Просмотр и печать не меняют документ.
          </Typography.Text>
        )}
        {pdfUrl && (
          <Button type="link" size="small" icon={<ExportOutlined />} href={pdfUrl} target="_blank" rel="noopener" style={{ paddingInline: 0 }} data-testid="docviewer-open-tab">
            Открыть PDF в новой вкладке
          </Button>
        )}
        {printNote && <Alert type={printNote.type} showIcon message={printNote.text} data-testid="docviewer-print-note" />}
      </div>

      {openLegacy && (
        <Button type="link" size="small" onClick={openLegacy} style={{ paddingInline: 0, alignSelf: "flex-start" }} data-testid="docviewer-legacy">
          Прежняя печатная форма (HTML)
        </Button>
      )}
    </div>
  );

  let main: React.ReactNode;
  if (!selected) {
    main = (
      <ProblemView
        problem={{ kind: "no_template", title: "Нет применимого макета", detail: "Для этого документа не опубликован ни один макет." }}
        legacy={openLegacy}
        canSeeTemplates={can("document_templates.read")}
        onNavigate={onClose}
      />
    );
  } else if (override || shown) {
    main = pdf.isError ? (
      <ProblemView problem={viewerProblem(pdf.error, navigator.onLine)} onRetry={() => pdf.refetch()} legacy={openLegacy} canSeeTemplates={can("document_templates.read")} onNavigate={onClose} />
    ) : pdfUrl ? (
      <iframe className="wms-docviewer-pdf" src={pdfUrl} title={`PDF: ${heading}`} data-testid="docviewer-pdf" />
    ) : (
      <div className="wms-docviewer-state" data-testid="docviewer-state-pdf-loading">
        <Spin size="large" />
        <Typography.Text type="secondary">Загружаем PDF…</Typography.Text>
      </div>
    );
  } else if (selected.version === null) {
    main = (
      <ProblemView
        problem={{ kind: "no_template", title: "Нет применимого макета", detail: `У макета «${selected.name}» нет опубликованной версии на дату документа. Опубликуйте версию в «Администрирование → Шаблоны документов» или выберите другой макет.` }}
        legacy={openLegacy}
        canSeeTemplates={can("document_templates.read")}
        onNavigate={onClose}
      />
    );
  } else if (missing.length) {
    main = (
      <div className="wms-docviewer-missing" data-testid="docviewer-state-missing">
        <Alert
          type="error"
          showIcon
          message="Не хватает данных для выбранного макета"
          description={channel === "portal" ? "Документ сформирует склад, когда данные будут заполнены." : "Дополните реквизиты или обратитесь к ответственному сотруднику. Система не подставляет вымышленные значения."}
          style={{ marginBottom: 12 }}
        />
        <MissingTable missing={missing} channel={channel} onNavigate={onClose} />
        {options.length > 1 && (
          <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
            Внутренний операционный лист можно выбрать слева — он не заменяет учётный макет.
          </Typography.Paragraph>
        )}
      </div>
    );
  } else if (formProblem) {
    main = <ProblemView problem={formProblem} onRetry={() => form.mutate(selected)} legacy={openLegacy} canSeeTemplates={can("document_templates.read")} onNavigate={onClose} />;
  } else {
    main = (
      <div className="wms-docviewer-state" data-testid="docviewer-state-forming">
        <Spin size="large" />
        <Typography.Text type="secondary">Формируем PDF по макету «{selected.name}»… Документ при этом не меняется.</Typography.Text>
      </div>
    );
  }

  const view = (
    <div className="wms-docviewer-grid">
      {side}
      <div className="wms-docviewer-main">{main}</div>
    </div>
  );

  const historyView = (
    <div data-testid="docviewer-history">
      {history.isError ? (
        <Alert type="error" showIcon message="История не загрузилась" description={viewerProblem(history.error, navigator.onLine).detail} />
      ) : (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Подтверждённые действия с файлами документа. «Открыт диалог печати» — не подтверждение физической печати.
          </Typography.Paragraph>
          <Table
            size="small"
            rowKey={(r, i) => `${r.rendition_id}-${r.at}-${r.action}-${i}`}
            loading={history.isLoading}
            dataSource={history.data?.events ?? []}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            scroll={{ x: true }}
            locale={{ emptyText: "Действий с файлами ещё не было" }}
            data-testid="docviewer-history-events"
            columns={[
              { title: "Время", dataIndex: "at", render: (v: string) => formatDateTime(v), width: 150 },
              { title: "Сотрудник", dataIndex: "actor", render: (v: string | null) => v ?? "—" },
              { title: "Действие", dataIndex: "label", render: (v: string, r) => <span data-action={r.action}>{v}</span> },
              {
                title: "Макет",
                key: "template",
                render: (_: unknown, r) => {
                  const row = history.data?.renditions.find((x) => x.id === r.rendition_id);
                  return row ? `${row.template_name ?? "—"}, версия ${row.template_version}` : r.template_version ? `версия ${r.template_version}` : "—";
                },
              },
            ]}
          />
          <Typography.Title level={5} style={{ marginTop: 16 }}>
            Сохранённые файлы
          </Typography.Title>
          <Table<Rendition>
            size="small"
            rowKey="id"
            loading={history.isLoading}
            dataSource={history.data?.renditions ?? []}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            scroll={{ x: true }}
            locale={{ emptyText: "Файлов ещё нет" }}
            data-testid="docviewer-history-files"
            columns={[
              { title: "Сформирован", dataIndex: "created_at", render: (v: string) => formatDateTime(v), width: 150 },
              { title: "Макет", dataIndex: "template_name", render: (v: string | null, r) => `${v ?? "—"}, версия ${r.template_version}` },
              { title: "Страниц", dataIndex: "pages", width: 90 },
              { title: "Сформировал", dataIndex: "created_by", render: (v: string | null) => v ?? "—" },
              {
                title: "",
                key: "open",
                render: (_: unknown, r) =>
                  r.id === shown?.id ? (
                    <Tag color="blue">показан</Tag>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        setOverride(r);
                        setTab("view");
                      }}
                      data-testid={`docviewer-history-open-${r.id}`}
                    >
                      Открыть
                    </Button>
                  ),
              },
            ]}
          />
        </>
      )}
    </div>
  );

  return (
    <div data-testid="docviewer" data-rendition-id={shown?.id ?? ""} data-sha256={fileSha ?? ""} data-narrow={narrow || undefined}>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 4, paddingRight: 32 }}>
        Документы и печать · {heading}
      </Typography.Title>
      {channel === "staff" ? (
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "view", label: "Документ", children: view },
            { key: "history", label: <span data-testid="docviewer-tab-history"><HistoryOutlined /> История</span>, children: historyView },
          ]}
        />
      ) : (
        view
      )}
    </div>
  );
}
